import { Box, Card, CardContent, Typography, Stack, Grid, Button } from "@mui/material";
import { Donut } from "../../v2/insights/Summary/componentUtils/Donut";
import { ClassExtended, InsightsCtx, UserCtx } from "../../../interfaces";
import { makeStyles } from "@mui/styles";
import { ReactComponent as IconBackground } from "../../../assets/icon-course-background.svg";
import { UserContext } from "../../../context/UserContext";
import { useContext } from "react";
import { InsightsContext } from "../../../context/InsightsContext";
import axios from "../../../../src/services/axios";
import { useHistory } from "react-router-dom";
import { MacsAuthenticator } from "../../../hooks/useMacsAuthenticator";
import { Role } from "../../../enums";

const useStyle = makeStyles((theme) => ({
  fafwIcon: {
    "&:before": {
      width: "14px",
      height: "12px",
    },
  },
  cardContainer: {
    minWidth: "378px",
    minHeight: "104px",
    width: "100%",
    padding: "24px",
    transition: "box-shadow 200ms ease-in-out",
    cursor: "pointer",
    "&:hover": {
      boxShadow: "0px 8px 12px #00000029",
    },
  },
  ellipsisText: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  gridContainer: {
    padding: "0px 24px",
    height: "68px",
    width: "100%",
    borderTop: `2px solid ${theme.palette.divider}`,
    cursor: "pointer",
    transition: "background-color 200ms ease-in-out",
    "&:hover": {
      backgroundColor: "#0000000A",
      "& .title": {
        textDecoration: "underline",
      },
    },
  },
  gridRow: {
    padding: "0px",
  },
  gridItem: {
    padding: "0px",
    display: "flex",
    alignItems: "center",
  },
}));

interface ClassListCardProps {
  classExtended: ClassExtended;
  variant?: "card" | "grid";
  percentage?: number;
  onClick: (navigateTo: string, classTitle: string) => void;
}

export const ClassListCard = (props: ClassListCardProps) => {
  const { classExtended, variant = "card", percentage = 0, onClick } = props;
  const history = useHistory();
  const classes = useStyle();
  const userCtx = useContext(UserContext) as UserCtx;
  const insightsCtx = useContext(InsightsContext) as InsightsCtx;

  const getIcon = () => {
    return (
      <Box sx={{ position: "relative" }}>
        <IconBackground
          style={{ color: classExtended.course_metadata?.color ?? "#65A11B" }}
        />
        <Box
          component="img"
          sx={{
            height: 32,
            width: 32,
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%,-50%)",
          }}
          alt={classExtended.course_name}
          src={
            classExtended.course_metadata?.icon ??
            "https://macs-assets.s3.amazonaws.com/pre-algebra-icon.svg"
          }
        />
      </Box>
    );
  };
  const courseIcon = getIcon();

  const classTitle = `${classExtended.course_name} ${classExtended.period_block} ${
    classExtended.start_date &&
    new Date(classExtended.start_date).toLocaleDateString("en-US", {
      timeZone: "UTC",
    })
  }`;

  const handleClick = () => {
    onClick(insightsCtx.getClassUrl(classExtended.id), classTitle);
  };

  const handleStartStudentView = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    classId: number
  ) => {
    e.stopPropagation();

    try {
      const res = await axios.post(`/user/start-student-view`, null, {
        params: {
          launchedClassId: classId,
          ltik: MacsAuthenticator.authHook.ltik,
        },
      });

      if (res.status === 200) {
        const newClassId = res.data?.newClassId;
        const launchedAssignmentId = res.data?.launchedAssignmentId;
        if (launchedAssignmentId)
          MacsAuthenticator.authHook.setLaunchedAssignmentId(launchedAssignmentId);
        MacsAuthenticator.authHook.setIsStudentView("true");
        //Set the role on the frontend temporarily
        userCtx.dbUser.role = Role.student;
        history.push({
          pathname: "/learning-overview",
          search: new URLSearchParams({
            classId: newClassId,
          }).toString(),
        });

        window.location.reload();
      }
    } catch (error) {
      console.error("Failed to start student view", error);
      throw error;
    }
  };

  if (variant === "grid") {
    return (
      <Stack
        direction="row"
        alignItems="center"
        className={classes.gridContainer}
        onClick={handleClick}>
        <Typography
          variant="body1"
          fontWeight={600}
          component="div"
          sx={{ minWidth: "280px", marginRight: "24px" }}
          className={`title ${classes.ellipsisText}`}>
          {classTitle}
        </Typography>
        <Grid container className={classes.gridRow}>
          <Grid item xs={4} className={classes.gridItem}>
            <Typography
              className={classes.ellipsisText}
              variant="body2"
              component="span"
              fontSize="14px">
              {classExtended.course_name}
            </Typography>
          </Grid>
          <Grid item xs={4} className={classes.gridItem}>
            <Typography
              className={classes.ellipsisText}
              variant="body2"
              component="span"
              fontSize="14px">
              {classExtended.teacher_name}
            </Typography>
          </Grid>
          <Grid item xs={4} className={classes.gridItem}>
            <Typography
              sx={{ marginLeft: "auto" }}
              className={classes.ellipsisText}
              variant="body2"
              component="span"
              fontSize="14px">
              {classExtended.observer_ids?.includes(userCtx.dbUser?.id) ? (
                <span>
                  <i
                    className={`far fa-eye fa-fw ${classes.fafwIcon}`}
                    style={{ marginRight: "8" }}></i>{" "}
                  Observer
                </span>
              ) : (
                ""
              )}
            </Typography>
          </Grid>
        </Grid>
      </Stack>
    );
  } else {
    return (
      <Card className={classes.cardContainer} onClick={handleClick}>
        <CardContent
          sx={{
            height: "100%",
            padding: "0px !important",
            display: "flex",
            flexDirection: "column",
            alignItems: "left",
          }}>
          <Box sx={{ display: "flex", flexDirection: "row", gap: "16px", width: "100%" }}>
            <Box sx={{ width: "56px", height: "56px" }}>{courseIcon}</Box>
            <Stack
              direction="column"
              sx={{
                flexGrow: 1,
                flexBasis: "0",
                flexShrink: 1,
                overflow: "hidden",
                marginRight: "auto",
              }}>
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: "bold",
                  width: "100%",
                  marginBottom: "6px",
                }}
                className={classes.ellipsisText}
                variant="h4"
                component="span">
                {classExtended.settings?.nickname ?? classTitle}
              </Typography>
              <Stack direction="row">
                <Typography
                  sx={{
                    fontSize: "14px",
                    marginRight: "26px",
                  }}
                  variant="body2"
                  component="span">
                  <i
                    className={`fa-regular fa-book fa-fw ${classes.fafwIcon}`}
                    style={{ marginRight: "10px" }}></i>{" "}
                  {classExtended.course_name}
                </Typography>
                {userCtx.dbUser?.id !== classExtended.teacher_id && (
                  <Typography
                    sx={{
                      fontSize: "14px",
                    }}
                    variant="body2"
                    component="span">
                    <i
                      className={`fa-regular fa-user fa-fw ${classes.fafwIcon}`}
                      style={{ marginRight: "10px" }}></i>{" "}
                    {classExtended.teacher_name}
                  </Typography>
                )}
              </Stack>
            </Stack>
            <Donut
              medium={true}
              value={Math.round(percentage * 100)}
              label={"Label"}
              aria-label={"Label"}
            />
            <Stack>
              <Button
                variant="contained"
                color="primary"
                sx={{
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: "#1565c0",
                    transform: "scale(1.03)",
                  },
                }}
                onClick={(e) => handleStartStudentView(e, props.classExtended.id)}>
                Student View{" "}
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    );
  }
};
